import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";

// "Concept" used to hardcode /concept/kebab and hide burger/tacos. With more
// than one concept available, the main nav cannot pick a favourite — concepts
// are now surfaced via the footer "Concept" column (all of them, with proper
// labels). The main nav stays short and intent-driven.
const LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/catalogue", label: "Catalogue" },
  { to: "/a-propos", label: "À propos" },
  { to: "/blog", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

/**
 * NavbarV2 — premium sticky navbar with blur on scroll, golden underline on active.
 * Mobile: fullscreen menu with giant typo.
 */
export const NavbarV2 = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  return (
    <header
      data-testid="navbar-v2"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
        scrolled ? "bg-fd-bg/85 backdrop-blur-2xl border-b border-fd-border shadow-[0_8px_40px_-12px_rgba(0,0,0,0.8)]" : "bg-transparent"
      }`}
    >
      <nav className={`flex items-center justify-between gap-3 max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 transition-all duration-500 ${scrolled ? "py-2 lg:py-3" : "py-3 lg:py-4"}`}>
        {/*
          Brand — image logo. Sized up ~×1.7 vs previous (was h-10/11/12,
          now h-16/18/20) so the wordmark dominates the navbar without
          fighting the Devis CTA. Reduced py-* on the <nav> compensates
          so the overall header height stays close to before (mobile ~80px,
          desktop ~100px). Mobile breakpoint stays safe: at 360 the logo
          is 64px tall + 28px burger + 40px padding = fits comfortably.
        */}
        <Link to="/" data-cursor-hover data-testid="nav-logo" aria-label="Frites Dorées — accueil" className="flex items-center gap-2 sm:gap-2.5 group shrink min-w-0">
          <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-fd-gold opacity-50 group-hover:animate-ping"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-fd-gold"></span>
          </span>
          <picture>
            <source srcSet="/brand/logo-frites-dorees.webp" type="image/webp" />
            <img
              src="/brand/logo-frites-dorees.png"
              alt="Frites Dorées"
              width={80}
              height={80}
              className="h-16 sm:h-[72px] md:h-20 w-auto object-contain select-none"
              draggable="false"
            />
          </picture>
        </Link>

        <ul className="hidden lg:flex items-center gap-12">
          {LINKS.map((l) => (
            <li key={l.to} className={l.to === "/catalogue" ? "relative group/cat" : undefined}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                data-cursor-hover
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) => `relative font-mono text-xs tracking-[0.18em] uppercase transition-colors ${isActive ? "text-fd-gold" : "text-fd-text/80 hover:text-fd-text"}`}
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span className={`absolute left-0 -bottom-1 h-px bg-fd-gold transition-all duration-500 ${isActive ? "w-full" : "w-0"}`}></span>
                  </>
                )}
              </NavLink>
              {l.to === "/catalogue" && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-1 group-hover/cat:opacity-100 group-hover/cat:visible group-hover/cat:translate-y-0 group-focus-within/cat:opacity-100 group-focus-within/cat:visible transition-all duration-300 z-50">
                  <div className="min-w-[230px] bg-[#0b0b0b] border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.6)] py-2">
                    <Link to="/catalogue" data-cursor-hover className="block px-5 py-3 font-mono text-[11px] tracking-[0.16em] uppercase text-fd-text/80 hover:text-fd-gold hover:bg-white/[0.03] transition-colors">
                      Nos produits
                    </Link>
                    <Link to="/catalogue-feuilletable" data-cursor-hover data-testid="nav-catalog-flip" className="flex items-center justify-between gap-3 px-5 py-3 font-mono text-[11px] tracking-[0.16em] uppercase text-fd-text/80 hover:text-fd-gold hover:bg-white/[0.03] transition-colors">
                      Catalogue feuilletable <ArrowUpRight size={12} />
                    </Link>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-5">
          <a href="tel:+33148680303" data-cursor-hover className="mono-label flex items-center gap-1.5 hover:text-fd-gold transition-colors">
            <Phone size={12} /> 01 48 68 03 03
          </a>
          <Link to="/contact" data-cursor-hover data-testid="nav-cta" className="btn-pill btn-primary-v2 !py-2.5 !px-5 !text-[11px]">
            Devis <ArrowUpRight size={13} />
          </Link>
        </div>

        <button
          data-cursor-hover
          onClick={() => setOpen(true)}
          className="lg:hidden p-2 -mr-2 text-fd-text shrink-0"
          aria-label="Ouvrir menu"
        >
          <Menu size={22} />
        </button>
      </nav>

      {/* Mobile menu — portalled to body to escape the fixed <header>
          containing block (otherwise inset-0 only covers the navbar's
          62px height). Solid bg-fd-bg, full viewport, z above everything. */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-[100] lg:hidden flex flex-col px-6 py-6 bg-fd-bg"
                style={{ backgroundColor: "#0A0A0A" }}
                data-testid="mobile-menu-overlay"
              >
                <div className="flex justify-between items-center mb-12">
                  <Link to="/" onClick={() => setOpen(false)} aria-label="Frites Dorées — accueil" className="shrink-0">
                    <picture>
                      <source srcSet="/brand/logo-frites-dorees.webp" type="image/webp" />
                      <img
                        src="/brand/logo-frites-dorees.png"
                        alt="Frites Dorées"
                        width={48}
                        height={48}
                        className="h-11 w-auto object-contain select-none"
                        draggable="false"
                      />
                    </picture>
                  </Link>
                  <button onClick={() => setOpen(false)} className="p-2 text-fd-text" aria-label="Fermer">
                    <X size={24} />
                  </button>
                </div>
                <ul className="flex flex-col gap-4">
                  {LINKS.map((l, i) => (
                    <motion.li
                      key={l.to}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.06 }}
                    >
                      <Link
                        to={l.to}
                        onClick={() => setOpen(false)}
                        className="font-display text-4xl uppercase tracking-tight hover:text-fd-gold transition-colors"
                      >
                        {l.label}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
                <div className="mt-auto space-y-3">
                  <a
                    href="tel:+33148680303"
                    className="btn-pill btn-secondary-v2 w-full"
                    onClick={() => setOpen(false)}
                  >
                    <Phone size={14} /> 01 48 68 03 03
                  </a>
                  <Link
                    to="/catalogue-feuilletable"
                    onClick={() => setOpen(false)}
                    className="btn-pill btn-secondary-v2 w-full"
                    data-testid="mobile-menu-catalog-cta"
                  >
                    Catalogue feuilletable
                  </Link>
                  <Link
                    to="/contact"
                    onClick={() => setOpen(false)}
                    className="btn-pill btn-primary-v2 w-full"
                  >
                    Devis sous 24h <ArrowUpRight size={14} />
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </header>
  );
};
